import {
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useForm } from 'react-hook-form';
import {
    Link,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
    ArrowLeft,
    EyeIcon,
    EyeOffIcon,
    LockKeyhole,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { baseRequest } from '@/lib/base';
import { passwordField } from '@/lib/passwordSchema';
import { cn } from '@/lib/utils';

const newPasswordSchema = z.object({
    password: passwordField,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
});

type NewPasswordFormData = z.infer<typeof newPasswordSchema>

const setNewPassword = async (payload: {
    email: string;
    new_password: string;
    confirm_new_password: string;
}) => {
    const response = await baseRequest({
        url: '/system/auth/new-password/',
        method: 'POST',
        body: JSON.stringify(payload),
        contentType: 'application/json',
    });
    return response;
};

export default function NewPassword() {
    const navigate = useNavigate();
    const location = useLocation();
    const email = (location.state as { email?: string })?.email;
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);

    useEffect(() => {
        if (!email) {
            navigate('/forgot-password', { replace: true });
        }
    }, [email, navigate]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<NewPasswordFormData>({
        resolver: zodResolver(newPasswordSchema),
    });

    const newPasswordMutation = useMutation({
        mutationFn: setNewPassword,
        onSuccess: (response) => {
            if (response.ok) {
                toast.success(response.data?.detail || 'Password changed successfully!');
                navigate('/login', { replace: true });
            } else {
                toast.error(response.data?.detail || 'Failed to change password. Please try again.');
            }
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });

    const onSubmit = useCallback((data: NewPasswordFormData) => {
        newPasswordMutation.mutate({
            email: email!,
            new_password: data.password,
            confirm_new_password: data.confirmPassword,
        });
    }, [email, newPasswordMutation]);

    if (!email) return null;

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Top nav */}
            <div className="p-6">
                <Link
                    to="/verify-otp"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Go Back
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className="flex flex-col gap-6 w-full max-w-sm">
                    {/* Logo and header */}
                    <div className="flex flex-col items-center gap-3">
                        <Link to="/">
                            <img src="/chanco-vertical.png" alt="Chanco" className="h-24 w-auto" />
                        </Link>
                        <h1 className="text-2xl font-bold text-foreground">New Password</h1>
                        <p className="text-sm text-muted-foreground text-center">
                            Enter your new password below
                        </p>
                    </div>

                    <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="grid gap-5">
                                    {/* Password field */}
                                    <div className="grid gap-2">
                                        <Label htmlFor="password">Password</Label>
                                        <div className="relative">
                                            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="password"
                                                type={isPasswordVisible ? 'text' : 'password'}
                                                placeholder="Enter new password"
                                                {...register('password')}
                                                className={cn(
                                                    'pl-10 pr-10',
                                                    { 'focus-visible:ring-destructive': errors.password },
                                                )}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground focus:outline-none"
                                                onClick={() => setIsPasswordVisible(
                                                    (prev) => !prev,
                                                )}
                                                aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                                            >
                                                {isPasswordVisible ? <EyeIcon className="h-4 w-4" /> : <EyeOffIcon className="h-4 w-4" />}
                                            </button>
                                        </div>
                                        {errors.password && <span className="text-sm text-destructive">{errors.password.message}</span>}
                                    </div>

                                    {/* Confirm password field */}
                                    <div className="grid gap-2">
                                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                                        <div className="relative">
                                            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="confirmPassword"
                                                type={isConfirmVisible ? 'text' : 'password'}
                                                placeholder="Confirm new password"
                                                {...register('confirmPassword')}
                                                className={cn(
                                                    'pl-10 pr-10',
                                                    { 'focus-visible:ring-destructive': errors.confirmPassword },
                                                )}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground focus:outline-none"
                                                onClick={() => setIsConfirmVisible((prev) => !prev)}
                                                aria-label={isConfirmVisible ? 'Hide password' : 'Show password'}
                                            >
                                                {isConfirmVisible ? <EyeIcon className="h-4 w-4" /> : <EyeOffIcon className="h-4 w-4" />}
                                            </button>
                                        </div>
                                        {errors.confirmPassword && <span className="text-sm text-destructive">{errors.confirmPassword.message}</span>}
                                    </div>

                                    {/* Change Password button */}
                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={newPasswordMutation.isPending}
                                    >
                                        {newPasswordMutation.isPending ? 'Changing...' : 'Change Password'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
